import CryptoJS from "crypto-js";
import { env } from "@/config/env";

const SECRET_KEY = env.COOKIE_SECRET;

const getRootDomain = (): string => {
  const hostname = window.location.hostname;

  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname))
    return hostname;

  const parts = hostname.split(".");

  if (parts.length <= 2) return hostname;

  return parts.slice(-2).join(".");
};

const Cookies = {
  setCookie(
    name: string,
    value: unknown,
    days: number = 30,
    crossSubdomain: boolean = true
  ): void {
    try {
      let stringValue: string;

      if (value !== null && typeof value === "object")
        stringValue = JSON.stringify(value);
      else stringValue = String(value);

      const encryptedValue = CryptoJS.AES.encrypt(
        stringValue,
        SECRET_KEY
      ).toString();

      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      const expires = `expires=${date.toUTCString()}`;

      const domain = crossSubdomain ? `.${getRootDomain()}` : "";
      const domainAttr = domain ? `domain=${domain};` : "";

      document.cookie = `${name}=${encryptedValue};${expires};path=/;${domainAttr}SameSite=Lax;${location.protocol === "https:" ? "Secure;" : ""
        }`;
    } catch (error) {
      console.error("Error setting cookie:", error);
    }
  },

  /**
   * Set a secure cookie with Strict SameSite for authentication tokens
   * Uses SameSite=Strict for maximum CSRF protection
   * Note: httpOnly cookies cannot be set via JavaScript — refresh token should be set by backend
   */
  setSecureCookie(
    name: string,
    value: unknown,
    days: number = 30,
    crossSubdomain: boolean = false
  ): void {
    try {
      let stringValue: string;

      if (value !== null && typeof value === "object")
        stringValue = JSON.stringify(value);
      else stringValue = String(value);

      const encryptedValue = CryptoJS.AES.encrypt(
        stringValue,
        SECRET_KEY
      ).toString();

      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      const expires = `expires=${date.toUTCString()}`;

      const domain = crossSubdomain ? `.${getRootDomain()}` : "";
      const domainAttr = domain ? `domain=${domain};` : "";

      // SameSite=Lax allows cookies on top-level navigations (needed for cross-port
      // app switching on localhost) while still protecting against CSRF on POST requests.
      // Secure flag always set for HTTPS
      document.cookie = `${name}=${encryptedValue};${expires};path=/;${domainAttr}SameSite=Lax;${location.protocol === "https:" ? "Secure;" : ""
        }`;
    } catch (error) {
      console.error("Error setting secure cookie:", error);
    }
  },

  getCookie<T = any>(name: string): T | null {
    try {
      const match = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${name}=`));

      if (!match) return null;
      // Use indexOf to avoid truncating base64 '=' padding in encrypted values
      const value = match.substring(match.indexOf("=") + 1);

      if (!value) return null;

      const bytes = CryptoJS.AES.decrypt(value, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      if (!decrypted) {
        console.warn(`Failed to decrypt cookie: ${name}`);
        return null;
      }

      try {
        return JSON.parse(decrypted) as T;
      } catch {
        return decrypted as T;
      }
    } catch (error) {
      console.error("Error getting cookie:", error);
      return null;
    }
  },

  getEncryptedCookie(name: string): string | null {
    try {
      const match = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${name}=`));

      if (!match) return null;
      return match.substring(match.indexOf("=") + 1) || null;
    } catch (error) {
      console.error("Error getting encrypted cookie:", error);
      return null;
    }
  },

  deleteCookie(name: string, crossSubdomain: boolean = true): void {
    try {
      const domain = crossSubdomain ? `.${getRootDomain()}` : "";
      const domainAttr = domain ? `domain=${domain};` : "";

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${domainAttr}`;

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
    } catch (error) {
      console.error("Error deleting cookie:", error);
    }
  },

  deleteAllCookies(crossSubdomain: boolean = true): void {
    try {
      const cookies = document.cookie.split(";");

      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
        this.deleteCookie(name, crossSubdomain);
      }
    } catch (error) {
      console.error("Error deleting all cookies:", error);
    }
  },

  testCookieAccess(): boolean {
    const testName = "__cookie_test__";
    this.setCookie(testName, "test", 1);
    const testValue = this.getCookie(testName);
    this.deleteCookie(testName);
    return testValue === "test";
  },
};

export default Cookies;
